import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Divider } from "primereact/divider";
import { useTheme } from "../../context/ThemeContext";
import { FaRegCalendarAlt, FaInfoCircle, FaCheckCircle } from "react-icons/fa";
import { PiWarningCircleBold } from "react-icons/pi";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getTemplates,
  getTemplatesbyId,
} from "../../reducers/templateReducer/templateActions";
import { FileUpload } from "primereact/fileupload";
import { Toast } from "primereact/toast";

const extractVariables = (text) => {
  const regex = /\[\[(.*?)\]\]|\{\{(.*?)\}\}|\[(.*?)\]/g;
  const variables = new Set();
  let match;
  while ((match = regex.exec(text)) !== null) {
    const varName = match[1] || match[2] || match[3];
    if (varName) variables.add(varName.trim());
  }
  return Array.from(variables);
};

const replaceVariables = (text, variables = {}) => {
  if (!text) return "";
  let replaced = text;
  Object.entries(variables).forEach(([key, value]) => {
    const regex = new RegExp(
      `\\[\\[${key}\\]\\]|\\{\\{${key}\\}\\}|\\[${key}\\]`,
      "g"
    );
    replaced = replaced.replace(regex, value || "");
  });
  return replaced;
};

const Step2Template = ({ meetingInfo, onNext, validateRef }) => {
  const toastRef = useRef(null);
  const { title } = meetingInfo;
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [coverImage, setCoverImage] = useState(null);

  const [showSetupModal, setShowSetupModal] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState({
    reminder: null,
    confirmation: null,
    booking: null,
  });
  const [allTemplates, setAllTemplates] = useState([]);
  const [templateData, setTemplateData] = useState({});
  const [previewAfterSave, setPreviewAfterSave] = useState({});
  const handleNext = () => {
    const allSelected =
      selectedTemplates.reminder &&
      selectedTemplates.confirmation &&
      selectedTemplates.booking;

    if (allSelected) {
      onNext({
        ...templateData,
        coverImage, // ✅ include coverImage in the payload
      });
    } else {
      toastRef.current.show({
        severity: "warn",
        summary: "Missing Template",
        detail: "Please select all templates before proceeding.",
      });
    }
  };

  useEffect(() => {
    if (validateRef) {
      validateRef.current = handleNext;
    }
  }, [validateRef, selectedTemplates, coverImage]);

  useEffect(() => {
    const fetchAllTemplates = async () => {
      let pageNo = 0;
      const limit = 100;
      let allTemplates = [];
      let hasMore = true;

      while (hasMore) {
        try {
          const result = await dispatch(
            getTemplates({ pageNo, limit, status: "APPROVED" })
          ).unwrap();
          const rows = result?.rows || [];

          if (rows.length > 0) {
            allTemplates = [...allTemplates, ...rows];
            pageNo++;
          } else {
            hasMore = false;
          }
        } catch (error) {
          console.error("Error fetching templates:", error);
          hasMore = false;
        }
      }

      const dropdownTemplates = allTemplates.map((tpl) => ({
        label: tpl.elementName,
        value: tpl.templateId,
      }));
      setAllTemplates(dropdownTemplates);
    };

    fetchAllTemplates();
  }, [dispatch]);

  const templates = [
    {
      key: "reminder",
      title: "Final Reminder",
      subtitle: "Sent before the meeting",
      icon: <FaInfoCircle size={18} color="#F97316" />,
      borderColor: "#F97316",
    },
    {
      key: "confirmation",
      title: "Meeting Cancellation",
      subtitle: "Notification for cancelled meeting",
      icon: <FaRegCalendarAlt size={18} color="#3B82F6" />,
      borderColor: "#3B82F6",
    },
    {
      key: "booking",
      title: "Appointment Confirmation",
      subtitle: "Final meeting details",
      icon: <FaCheckCircle size={18} color="#10B981" />,
      borderColor: "#10B981",
    },
  ];

  const fetchTemplatePreview = async (
    templateId,
    type,
    applyToPreview = false
  ) => {
    try {
      const tpl = await dispatch(getTemplatesbyId(templateId)).unwrap();
      const { containerMeta } = tpl || {};
      const parsed = containerMeta ? JSON.parse(containerMeta) : {};
      const variables = extractVariables(parsed.data || "");

      const result = {
        id: templateId || "",
        header: parsed.header || "",
        body: parsed.data || "",
        footer: parsed.footer || "",
        variables: variables.reduce((acc, curr) => {
          acc[curr] = "";
          return acc;
        }, {}),
      };

      setTemplateData((prev) => ({ ...prev, [type]: result }));

      if (applyToPreview) {
        setPreviewAfterSave((prev) => ({ ...prev, [type]: result }));
      }
    } catch (err) {
      console.error("Error loading template:", err);
    }
  };

  const handleSave = async () => {
    const updatedData = {};
    const updatedSelectedTemplates = {};

    for (const key of ["reminder", "confirmation", "booking"]) {
      const templateId = selectedTemplates[key];

      if (templateId) {
        try {
          const tpl = await dispatch(getTemplatesbyId(templateId)).unwrap();

          const { containerMeta } = tpl || {};
          if (!containerMeta) {
            console.error(
              `[handleSave] containerMeta missing for templateId=${templateId}`
            );
            continue;
          }

          const parsed = JSON.parse(containerMeta);

          const variables = extractVariables(parsed.data || "");

          updatedData[key] = {
            header: parsed.header || "",
            body: parsed.data || "",
            footer: parsed.footer || "",
            variables: variables.reduce((acc, curr) => {
              acc[curr] = "";
              return acc;
            }, {}),
          };

          updatedSelectedTemplates[key] = templateId;
        } catch (err) {
          console.error(`[handleSave] Error fetching preview for ${key}:`, err);
        }
      } else {
        updatedData[key] = null;
        updatedSelectedTemplates[key] = null;
      }
    }

    setSelectedTemplates(updatedSelectedTemplates);
    setTemplateData(updatedData);

    setShowSetupModal(false);
  };

  return (
    <>
      <Toast ref={toastRef} />
      <div
        style={{
          padding: "2rem",
          display: "flex",
          flexDirection: "column",
          gap: "2rem",
        }}
      >
        <div
          style={{
            backgroundColor: theme.base_background,
            borderRadius: "12px",
            padding: "1.5rem",
            boxShadow: "0 2px 6px rgba(27, 22, 22, 0.05)",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "0.85rem",
                color: theme.text_secondary,
                marginBottom: "0.25rem",
              }}
            >
              Meeting title
            </div>
            <div style={{ fontWeight: "600", fontSize: "1rem" }}>{title}</div>
          </div>
        </div>
        {/* <div style={{ marginTop: "1.5rem" }}>
          <div
            style={{
              fontSize: "0.85rem",
              color: theme.text_secondary,
              marginBottom: "0.25rem",
            }}
          >
            Upload Cover Image
          </div>

          <div
            style={{
              border: `1px dashed ${theme.text_secondary}`,
              borderRadius: "12px",
              padding: "1rem",
              backgroundColor: theme.surface,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            onClick={() => document.getElementById("cover-image-input").click()}
          >
            <span style={{ fontSize: "0.85rem", color: theme.text_secondary }}>
              Click to select a cover image
            </span>

            {coverImage && (
              <img
                src={coverImage}
                alt="Cover Preview"
                style={{
                  marginTop: "1rem",
                  maxHeight: "120px",
                  borderRadius: "8px",
                  objectFit: "cover",
                }}
              />
            )}
          </div>

          <input
            id="cover-image-input"
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                const reader = new FileReader();
                reader.onloadend = () => {
                  setCoverImage(reader.result); // base64 string
                };
                reader.readAsDataURL(file);
              }
            }}
          />
        </div> */}

        {/* Setup Buttons */}
        <div
          style={{
            display: "flex",
            gap: "2rem",
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          {/* Use Existing */}
          <div
            style={{
              flex: "1 1 48%",
              backgroundColor: theme.background,
              border: `1px solid ${theme.text_secondary}`,
              borderRadius: "12px",
              padding: "1.5rem",
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontWeight: 600,
              }}
            >
              <span
                style={{
                  backgroundColor: theme.secondary,
                  padding: "0.4rem",
                  borderRadius: "8px",
                }}
              >
                <svg
                  width="18"
                  height="18"
                  fill={theme.primary}
                  viewBox="0 0 24 24"
                >
                  <path d="M5 4v16h14V4H5zm12 14H7V6h10v12z" />
                  <path d="M8 8h8v2H8z" />
                </svg>
              </span>
              Configure Template (0/3)
            </div>
            <div style={{ fontSize: "0.9rem", color: theme.text_secondary }}>
              Use existing templates and customize them for this specific
              meeting.
            </div>
            <Button
              label="Set Up All Templates"
              onClick={() => setShowSetupModal(true)}
              style={{
                backgroundColor: theme.primary,
                border: "none",
                color: theme.sideBarText,
                marginTop: "0.5rem",
                alignSelf: "flex-start",
              }}
            />
          </div>

          {/* Create New */}
          <div
            style={{
              flex: "1 1 48%",
              backgroundColor: theme.background,
              border: `1px solid ${theme.text_secondary}`,
              borderRadius: "12px",
              padding: "1.5rem",
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontWeight: 600,
              }}
            >
              <span
                style={{
                  backgroundColor: theme.secondary,
                  padding: "0.4rem",
                  borderRadius: "8px",
                }}
              >
                <svg
                  width="18"
                  height="18"
                  fill={theme.primary}
                  viewBox="0 0 24 24"
                >
                  <path d="M11 11V6h2v5h5v2h-5v5h-2v-5H6v-2h5z" />
                </svg>
              </span>
              Create Template
            </div>
            <div style={{ fontSize: "0.9rem", color: theme.text_secondary }}>
              Start from scratch and create custom templates for your meeting
              communications.
            </div>
            <Button
              label="Create New Template"
              outlined
              style={{
                color: theme.primary,
                border: `1px solid ${theme.primary}`,
                marginTop: "0.5rem",
                alignSelf: "flex-start",
              }}
              onClick={() => navigate("/campaign-templates/new")}
            />
          </div>
        </div>

        {/* Template Cards */}
        <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
          {templates.map((temp, index) => (
            <div
              key={index}
              style={{
                flex: "1 1 300px",
                border: `2px dashed ${temp.borderColor}`,
                padding: "1.5rem",
                borderRadius: "10px",
                backgroundColor: theme.base_background,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                minHeight: "220px",
              }}
            >
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    marginBottom: "0.5rem",
                  }}
                >
                  {temp.icon}
                  <span style={{ fontWeight: 600 }}>{temp.title}</span>
                </div>
                <div
                  style={{ fontSize: "0.9rem", color: theme.text_secondary }}
                >
                  {temp.subtitle}
                </div>
              </div>

              <div style={{ marginTop: "1rem" }}>
                {templateData[temp.key] ? (
                  <div
                    style={{
                      marginTop: "0.5rem",
                      padding: "1rem",
                      borderRadius: "12px",
                      border: "1px solid #E5E7EB",
                      backgroundColor:
                        temp.key === "reminder"
                          ? "#FEF3C7"
                          : temp.key === "confirmation"
                          ? "#DBEAFE"
                          : "#E8FBEF",
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                    }}
                  >
                    <strong style={{ fontSize: "0.95rem" }}>
                      {templateData[temp.key].header}
                    </strong>

                    <p
                      style={{
                        whiteSpace: "pre-wrap",
                        fontSize: "0.9rem",
                        margin: 0,
                        marginTop: "0.75rem",
                      }}
                    >
                      {replaceVariables(
                        templateData[temp.key].body,
                        templateData[temp.key].variables
                      )}
                    </p>

                    {templateData[temp.key].footer && (
                      <small style={{ color: "#6B7280" }}>
                        {templateData[temp.key].footer}
                      </small>
                    )}
                    <div style={{ marginTop: "0.75rem" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                          background: "#F9FAFB",
                          border: "1px solid #D1D5DB",
                          borderRadius: "8px",
                          padding: "0.75rem",
                        }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 2fr",
                            fontSize: "0.85rem",
                            fontWeight: 500,
                            color: "#4B5563",
                            marginBottom: "0.25rem",
                          }}
                        >
                          <div>Variable</div>
                          <div>Value</div>
                        </div>

                        {Object.entries(
                          templateData[temp.key].variables || {}
                        ).map(([varName, value]) => (
                          <div
                            key={varName}
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 2fr",
                              alignItems: "center",
                              gap: "0.5rem",
                            }}
                          >
                            <div
                              style={{
                                backgroundColor: "#E5E7EB",
                                padding: "0.3rem 0.5rem",
                                borderRadius: "6px",
                                fontSize: "0.85rem",
                                fontFamily: "monospace",
                                color: "#374151",
                              }}
                            >
                              {varName}
                            </div>
                            <input
                              value={value}
                              onChange={(e) => {
                                setTemplateData((prev) => ({
                                  ...prev,
                                  [temp.key]: {
                                    ...prev[temp.key],
                                    variables: {
                                      ...prev[temp.key].variables,
                                      [varName]: e.target.value,
                                    },
                                  },
                                }));
                              }}
                              style={{
                                padding: "0.35rem 0.5rem",
                                fontSize: "0.85rem",
                                border: "1px solid #D1D5DB",
                                borderRadius: "6px",
                                width: "100%",
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        marginBottom: "0.5rem",
                        fontSize: "0.85rem",
                        color: theme.text_secondary,
                      }}
                    >
                      No Template Configured for {temp.title}
                    </div>
                    <Button
                      label="Set Up Template"
                      onClick={() => setShowSetupModal(true)}
                      style={{
                        backgroundColor: theme.secondary,
                        color: theme.text_primary,
                        border: "none",
                        width: "100%",
                      }}
                    />
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Buttons */}
        <div
          style={{
            marginTop: "2rem",
            display: "flex",
            justifyContent: "space-between",
          }}
        ></div>

        {/* {Object.entries(templateData).map(([key, data]) => (
          <div
            key={key}
            style={{
              border: "1px solid #D1D5DB",
              borderRadius: "10px",
              padding: "1rem",
              background: "#F9FAFB",
            }}
          >
            <div style={{ marginBottom: "0.5rem", fontWeight: 600 }}>
              {key.toUpperCase()} Preview:
            </div>
            <div style={{ whiteSpace: "pre-line", marginBottom: "0.75rem" }}>
              {replaceVariables(data.body, data.variables)}
            </div>
          </div>
        ))} */}
      </div>
      <Dialog
        header="Set Up All Template"
        visible={showSetupModal}
        onHide={() => setShowSetupModal(false)}
        style={{ width: "80vw" }}
        footer={
          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}
          >
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setShowSetupModal(false)}
              style={{ color: theme.text_secondary }}
            />
            <Button
              label="Save"
              onClick={handleSave}
              style={{
                backgroundColor: theme.primary,
                border: "none",
                color: theme.sideBarText,
              }}
            />
          </div>
        }
      >
        <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
          {templates.map((template) => (
            <div
              key={template.key}
              style={{
                flex: "1 1 30%",
                border: "1px solid #E5E7EB",
                borderRadius: "12px",
                padding: "1.25rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  {template.icon}
                  <div>
                    <div style={{ fontWeight: 600 }}>{template.title}</div>
                    <div style={{ fontSize: "0.85rem", color: "#6B7280" }}>
                      {template.subtitle}
                    </div>
                  </div>
                </div>
                {selectedTemplates[template.key] ? (
                  <FaCheckCircle color="#22C55E" />
                ) : (
                  <PiWarningCircleBold size={20} color="#F59E0B" />
                )}
              </div>

              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 500 }}>
                  Select Template
                </label>
                <Dropdown
                  value={selectedTemplates[template.key]}
                  options={allTemplates}
                  onChange={async (e) => {
                    setSelectedTemplates((prev) => ({
                      ...prev,
                      [template.key]: e.value,
                    }));
                    await fetchTemplatePreview(e.value, template.key, true);
                  }}
                  placeholder="Choose template"
                  style={{ width: "100%", marginTop: "0.25rem" }}
                />
              </div>

              {/* Preview Section */}
              {templateData[template.key] && (
                <div
                  style={{
                    marginTop: "1rem",
                    padding: "1rem",
                    borderRadius: "12px",
                    border: "1px solid #E5E7EB",
                    backgroundColor:
                      template.key === "reminder"
                        ? "#FEF3C7"
                        : template.key === "confirmation"
                        ? "#DBEAFE"
                        : "#E8FBEF",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  <strong style={{ fontSize: "0.95rem" }}>
                    {templateData[template.key].header}
                  </strong>
                  {/* Body */}
                  <p
                    style={{
                      whiteSpace: "pre-wrap",
                      fontSize: "0.9rem",
                      margin: 0,
                    }}
                  >
                    {templateData[template.key].body}
                  </p>

                  {/* Footer */}
                  {templateData[template.key].footer && (
                    <small style={{ color: "#6B7280" }}>
                      {templateData[template.key].footer}
                    </small>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        <Divider />
        <div
          style={{ fontSize: "0.85rem", color: "#6B7280", marginTop: "1rem" }}
        >
          <strong>Note:</strong> ⚠️ Variables cannot be edited here.
        </div>
      </Dialog>
    </>
  );
};

export default Step2Template;
